// src/pages/GmailSettingsPage.js
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ConnectGmailButton from '../components/leadsComponents/ConnectGmailButton';
import { performLogoutAndRedirect } from '../utils/logout';
import { FiLogOut, FiShield, FiUser, FiMail, FiCheckCircle } from 'react-icons/fi';

export default function GmailSettingsPage() {
  // grab the logged-in admin (host) from Redux
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { adminInfo } = useSelector(state => state.login);
  const isConnected = Boolean(adminInfo?.gmailConnected || adminInfo?.refreshToken);

  const handleLogout = () => {
    performLogoutAndRedirect(dispatch);
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Settings</h1>
          <p className="text-sm text-[#6B7280]">Profile, security, and integrations</p>
        </div>
        <button
          onClick={handleLogout}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 font-semibold text-sm"
        >
          <FiLogOut className="w-4 h-4" />
          Logout
        </button>
      </div>

      {/* Profile summary */}
      <section className="bg-white p-6 rounded-2xl shadow-sm border border-zinc-100">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white font-semibold">
            {adminInfo?.firstName?.[0]}{adminInfo?.lastName?.[0]}
          </div>
          <div className="flex-1">
            <p className="text-sm text-zinc-500">Signed in as</p>
            <p className="text-lg font-semibold text-zinc-900">
              {adminInfo?.firstName} {adminInfo?.lastName}
            </p>
            <p className="text-sm text-zinc-600">{adminInfo?.email}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
              <FiCheckCircle className="w-3 h-3" />
              SSO active
            </span>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold">
              <FiUser className="w-3 h-3" />
              {adminInfo?.role || 'Support'}
            </span>
          </div>
        </div>
      </section>

      {/* Gmail & security */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white p-6 rounded-2xl shadow-sm border border-zinc-100">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold text-[#1F2937]">Gmail Integration</h2>
              <p className="text-sm text-[#6B7280]">Shared inbox support@bellhopt.com</p>
            </div>
          </div>
          {isConnected ? (
            <div className="space-y-4">
              <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-emerald-700 text-sm font-semibold flex items-center gap-2">
                <FiMail className="w-4 h-4" />
                Gmail is connected
              </div>
              <div className="text-sm text-zinc-600">
                Sending as <span className="font-semibold text-zinc-900">{adminInfo?.gmailPrimaryAddress || adminInfo?.gmail || adminInfo?.email}</span>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-[#6B7280] mb-4">
                Connect your Gmail account so you can send outreach emails directly.
              </p>
              <ConnectGmailButton />
            </>
          )}
          {adminInfo?.gmailAliases?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-2">Send As Aliases</p>
              <div className="flex flex-wrap gap-2">
                {adminInfo.gmailAliases.map((alias) => (
                  <span
                    key={alias}
                    className="inline-flex items-center rounded-full bg-zinc-100 px-3 py-1 text-xs font-medium text-zinc-700"
                  >
                    {alias}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="bg-white p-6 rounded-2xl shadow-sm border border-zinc-100">
          <div className="flex items-center gap-2 mb-3">
            <FiShield className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-[#1F2937]">Security</h2>
          </div>
          <ul className="space-y-2 text-sm text-[#374151]">
            <li>• SSO enforced via Google Workspace</li>
            <li>• Session ends on logout or token expiry</li>
            <li>• Contact role/reason passed to tickets for audit</li>
          </ul>
          <button
            onClick={() => navigate('/settings')}
            className="mt-4 inline-flex items-center px-4 py-2 rounded-xl border border-zinc-200 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
          >
            View account activity
          </button>
        </section>
      </div>
    </div>
  );
}
